/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useState } from 'react'
import { css } from '@emotion/react'

import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiSplitPanel,
  EuiIcon,
  EuiText,
  EuiSkeletonText,
  EuiSpacer,
  EuiLink,
  useEuiTheme,
  EuiPanel,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { useListTrafficFilterRulesetsQuery } from '@modules/cloud-lib/networkSecurity/hooks'

import ProtectedResourcesModal from '../ProtectedResources/Modal'

const TrafficFilterSummary: React.FunctionComponent = () => {
  const { euiTheme } = useEuiTheme()

  const { data, isLoading } = useListTrafficFilterRulesetsQuery()
  const [modalRulesets, setModalRulesets] = useState<any[] | null>(null)

  const allRulesets = data?.rulesets || []

  // Calculate total and in-use IP filters
  const ipFilterRulesets = allRulesets.filter((ruleset) => ruleset.type === 'ip')
  const totalIpFilters = ipFilterRulesets.length
  const associatedIpFilters = ipFilterRulesets.filter(
    (ruleset) => ruleset.associations && ruleset.associations.length > 0,
  ).length

  // Calculate total associated deployments and projects for IP filters
  const associatedIpFilterDeployments = new Set<string>()
  const associatedIpFilterProjects = new Set<string>()

  ipFilterRulesets.forEach((ruleset) => {
    if (ruleset.associations) {
      ruleset.associations.forEach((association) => {
        if (association.id && association.entity_type) {
          if (association.entity_type === 'deployment') {
            associatedIpFilterDeployments.add(association.id)
          } else if (association.entity_type === 'project') {
            associatedIpFilterProjects.add(association.id)
          }
        }
      })
    }
  })

  const totalIpFilterAssociatedDeployments = associatedIpFilterDeployments.size
  const totalIpFilterAssociatedProjects = associatedIpFilterProjects.size

  // Calculate total and in-use Private Connections
  const privateConnectionRulesets = allRulesets.filter((ruleset) => ruleset.type !== 'ip')
  const totalPrivateConnections = privateConnectionRulesets.length
  const associatedPrivateConnections = privateConnectionRulesets.filter(
    (ruleset) => ruleset.associations && ruleset.associations.length > 0,
  ).length

  // Calculate total associated deployments and projects for Private Connections
  const associatedPrivateConnectionDeployments = new Set<string>()
  const associatedPrivateConnectionProjects = new Set<string>()

  privateConnectionRulesets.forEach((ruleset) => {
    if (ruleset.associations) {
      ruleset.associations.forEach((association) => {
        if (association.id && association.entity_type) {
          if (association.entity_type === 'deployment') {
            associatedPrivateConnectionDeployments.add(association.id)
          } else if (association.entity_type === 'project') {
            associatedPrivateConnectionProjects.add(association.id)
          }
        }
      })
    }
  })

  const totalPrivateConnectionAssociatedDeployments = associatedPrivateConnectionDeployments.size
  const totalPrivateConnectionAssociatedProjects = associatedPrivateConnectionProjects.size

  const rightBorderStyle = css`
    border-right: 1px solid ${euiTheme.colors.borderBasePlain};
    padding-right: ${euiTheme.size.m};
  `

  const ipFilterContent = isLoading ? (
    <EuiSkeletonText lines={2} />
  ) : (
    <EuiFlexGroup gutterSize='m' alignItems='flexStart'>
      <EuiFlexItem>
        <EuiPanel paddingSize='none' color='transparent' css={rightBorderStyle}>
          <EuiText>
            <strong>
              <FormattedMessage
                id='networkSecurity.ipFiltersSummary.totalIpFilters'
                defaultMessage='{count} {count, plural, one {IP filter} other {IP filters}}'
                values={{ count: totalIpFilters }}
              />
            </strong>
          </EuiText>
          <EuiText size='s'>
            <FormattedMessage
              id='networkSecurity.ipFiltersSummary.associatedIpFilters'
              defaultMessage='{associatedCount} of {totalCount} in use'
              values={{
                associatedCount: associatedIpFilters,
                totalCount: totalIpFilters,
              }}
            />
          </EuiText>
        </EuiPanel>
      </EuiFlexItem>
      <EuiFlexItem>
        <EuiSpacer size='xs' />
        <EuiText size='s' color='subdued'>
          <strong>
            <FormattedMessage
              id='networkSecurity.ipFiltersSummary.protectedResources'
              defaultMessage='Protected resources'
            />
          </strong>
        </EuiText>

        <EuiFlexGroup gutterSize='m' alignItems='flexStart'>
          <EuiFlexItem grow={false}>
            <EuiText size='s'>
              <EuiLink
                disabled={totalIpFilterAssociatedDeployments === 0}
                onClick={() => {
                  const filteredRulesets = ipFilterRulesets.filter(
                    (ruleset) =>
                      ruleset.associations &&
                      ruleset.associations.some(
                        (association) => association.entity_type === 'deployment',
                      ),
                  )
                  setModalRulesets(filteredRulesets)
                }}
              >
                <FormattedMessage
                  id='networkSecurity.ipFiltersSummary.associatedDeployments'
                  defaultMessage='{count} {count, plural, one {deployment} other {deployments}}'
                  values={{ count: totalIpFilterAssociatedDeployments }}
                />
              </EuiLink>
            </EuiText>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiText size='s'>
              <EuiLink
                disabled={totalIpFilterAssociatedProjects === 0}
                onClick={() => {
                  const filteredRulesets = ipFilterRulesets.filter(
                    (ruleset) =>
                      ruleset.associations &&
                      ruleset.associations.some(
                        (association) => association.entity_type === 'project',
                      ),
                  )
                  setModalRulesets(filteredRulesets)
                }}
              >
                <FormattedMessage
                  id='networkSecurity.ipFiltersSummary.associatedProjects'
                  defaultMessage='{count} {count, plural, one {project} other {projects}}'
                  values={{ count: totalIpFilterAssociatedProjects }}
                />
              </EuiLink>
            </EuiText>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlexItem>
    </EuiFlexGroup>
  )

  const privateConnectionContent = isLoading ? (
    <EuiSkeletonText lines={2} />
  ) : (
    <EuiFlexGroup gutterSize='m' alignItems='flexStart'>
      <EuiFlexItem>
        <EuiPanel paddingSize='none' color='transparent' css={rightBorderStyle}>
          <EuiText>
            <strong>
              <FormattedMessage
                id='networkSecurity.privateConnectionsSummary.totalPrivateConnections'
                defaultMessage='{count} {count, plural, one {Private connection} other {Private connections}}'
                values={{ count: totalPrivateConnections }}
              />
            </strong>
          </EuiText>
          <EuiText size='s'>
            <FormattedMessage
              id='networkSecurity.privateConnectionsSummary.associated'
              defaultMessage='{associatedCount} of {totalCount} in use'
              values={{
                associatedCount: associatedPrivateConnections,
                totalCount: totalPrivateConnections,
              }}
            />
          </EuiText>
        </EuiPanel>
      </EuiFlexItem>
      <EuiFlexItem css={rightBorderStyle}>
        <EuiSpacer size='xs' />
        <EuiText size='s' color='subdued'>
          <strong>
            <FormattedMessage
              id='networkSecurity.privateConnectionsSummary.protectedResources'
              defaultMessage='Protected resources'
            />
          </strong>
        </EuiText>
        <EuiFlexGroup gutterSize='m' alignItems='flexStart'>
          <EuiFlexItem grow={false}>
            <EuiText size='s'>
              <EuiLink
                disabled={totalPrivateConnectionAssociatedDeployments === 0}
                onClick={() => {
                  const filteredRulesets = privateConnectionRulesets.filter(
                    (ruleset) =>
                      ruleset.associations &&
                      ruleset.associations.some(
                        (association) => association.entity_type === 'deployment',
                      ),
                  )
                  setModalRulesets(filteredRulesets)
                }}
              >
                <FormattedMessage
                  id='networkSecurity.privateConnectionsSummary.associatedDeployments'
                  defaultMessage='{count} {count, plural, one {deployment} other {deployments}}'
                  values={{ count: totalPrivateConnectionAssociatedDeployments }}
                />
              </EuiLink>
            </EuiText>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiText size='s'>
              <EuiLink
                disabled={totalPrivateConnectionAssociatedProjects === 0}
                onClick={() => {
                  const filteredRulesets = privateConnectionRulesets.filter(
                    (ruleset) =>
                      ruleset.associations &&
                      ruleset.associations.some(
                        (association) => association.entity_type === 'project',
                      ),
                  )
                  setModalRulesets(filteredRulesets)
                }}
              >
                <FormattedMessage
                  id='networkSecurity.privateConnectionsSummary.associatedProjects'
                  defaultMessage='{count} {count, plural, one {project} other {projects}}'
                  values={{ count: totalPrivateConnectionAssociatedProjects }}
                />
              </EuiLink>
            </EuiText>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlexItem>
    </EuiFlexGroup>
  )

  return (
    <React.Fragment>
      <EuiFlexGroup gutterSize='m' alignItems='stretch'>
        <EuiFlexItem data-test-subj='ipFiltersSummary'>
          <EuiSplitPanel.Outer hasBorder={true}>
            <EuiSplitPanel.Inner grow={false} color='subdued' paddingSize='s'>
              <EuiFlexGroup gutterSize='s' alignItems='center'>
                <EuiFlexItem grow={false}>
                  <EuiIcon type='logstashFilter' />
                </EuiFlexItem>
                <EuiFlexItem>
                  <EuiText size='s'>
                    <FormattedMessage
                      id='networkSecurity.ipFiltersSummary.title'
                      defaultMessage='IP filters usage summary'
                    />
                  </EuiText>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiSplitPanel.Inner>
            <EuiSplitPanel.Inner paddingSize='m'>
              <EuiFlexGroup gutterSize='m' alignItems='stretch'>
                <EuiFlexItem>{ipFilterContent}</EuiFlexItem>
              </EuiFlexGroup>
            </EuiSplitPanel.Inner>
          </EuiSplitPanel.Outer>
        </EuiFlexItem>
        <EuiFlexItem data-test-subj='privateConnectionsSummary'>
          <EuiSplitPanel.Outer hasBorder={true}>
            <EuiSplitPanel.Inner grow={false} color='subdued' paddingSize='s'>
              <EuiFlexGroup gutterSize='s' alignItems='center'>
                <EuiFlexItem grow={false}>
                  <EuiIcon type='lock' />
                </EuiFlexItem>
                <EuiFlexItem>
                  <EuiText size='s'>
                    <FormattedMessage
                      id='networkSecurity.privateConnectionsSummary.title'
                      defaultMessage='Private connections usage summary'
                    />
                  </EuiText>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiSplitPanel.Inner>
            <EuiSplitPanel.Inner paddingSize='m'>
              <EuiFlexGroup gutterSize='m' alignItems='stretch'>
                <EuiFlexItem>{privateConnectionContent}</EuiFlexItem>
              </EuiFlexGroup>
            </EuiSplitPanel.Inner>
          </EuiSplitPanel.Outer>
        </EuiFlexItem>
      </EuiFlexGroup>

      {modalRulesets && (
        <ProtectedResourcesModal
          rulesets={modalRulesets}
          onClose={() => {
            setModalRulesets(null)
          }}
        />
      )}
    </React.Fragment>
  )
}

export default TrafficFilterSummary
